import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatDate, formatDayCount, formatMoney } from "../lib/format";
import { PAYROLL_LABEL, PAYROLL_TONE, formatMinutes, isAccountAdmin, isManagerOrAbove } from "../lib/people";
import type { PayrollItem, PayrollRun } from "../api/types";

interface RunTotals {
  employee_count: number;
  gross: string;
  net_pay: string;
  days_worked: string;
}

/** The month-end register: compute, approve, pay, lock. Money moves here. */
export function PayrollPage() {
  const { user } = useAuth();
  const isAdmin = isAccountAdmin(user?.role);
  const isManager = isManagerOrAbove(user?.role);

  if (!isManager) {
    return (
      <>
        <PageHeader title="Payroll" subtitle="Monthly runs, from compute to paid." />
        <Card>
          <EmptyState
            title="Managers only"
            body="Your own payslips are on your desk. The whole register is for people who run the payroll."
          />
        </Card>
      </>
    );
  }

  return <PayrollManagerView isAdmin={isAdmin} timezone={user?.account?.timezone} currency={user?.account?.currency} />;
}

function PayrollManagerView({
  isAdmin,
  timezone,
  currency,
}: {
  isAdmin: boolean;
  timezone?: string | null;
  currency?: string | null;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ["payroll-runs", 50],
    queryFn: ({ signal }) => api.payrollRuns({ page_size: 50 }, signal),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["payroll-runs"] });

  const create = useMutation({
    mutationFn: (periodStart: string) => api.createPayrollRun({ period_start: periodStart }),
    onSuccess: invalidate,
  });

  const transition = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "compute" | "approve" | "pay" | "lock" | "cancel" }) => {
      if (action === "compute") return api.computePayrollRun(id);
      if (action === "approve") return api.approvePayrollRun(id);
      if (action === "pay") return api.payPayrollRun(id);
      if (action === "lock") return api.lockPayrollRun(id);
      return api.cancelPayrollRun(id);
    },
    onSuccess: invalidate,
  });

  const rows = runs.data?.results ?? [];

  return (
    <>
      <PageHeader
        title="Payroll"
        subtitle="A run moves draft → computed → approved → paid. Lock closes the books for the period."
        actions={<CreateRun pending={create.isPending} onSubmit={(period) => create.mutate(period)} />}
      />

      {create.error || transition.error ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {errorMessage(create.error) ?? errorMessage(transition.error)}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {runs.isPending ? (
          <TableSkeleton rows={6} />
        ) : runs.isError ? (
          <EmptyState title="Could not load payroll runs" body="Please try again." />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Plus size={28} />}
            title="No payroll runs yet"
            body="Start one with a period start month; the pay date defaults to the end of the month."
          />
        ) : (
          <table className="hidden w-full sm:table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5" />
                <th className="px-4 py-2.5">Run</th>
                <th className="px-4 py-2.5">Period</th>
                <th className="px-4 py-2.5 text-right">Employees</th>
                <th className="px-4 py-2.5 text-right">Gross</th>
                <th className="px-4 py-2.5 text-right">Net pay</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((run) => {
                const totals = (run.totals ?? {}) as unknown as RunTotals;
                const active = expanded === run.id;
                return (
                  <RunRow
                    key={run.id}
                    run={run}
                    totals={totals}
                    active={active}
                    canChain={transitions(run.status, isAdmin)}
                    transitioning={transition.isPending}
                    timezone={timezone}
                    currency={currency}
                    onToggle={() => setExpanded(active ? null : run.id)}
                    onTransition={(action) => transition.mutate({ id: run.id, action })}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function RunRow({
  run,
  totals,
  active,
  canChain,
  transitioning,
  timezone,
  currency,
  onToggle,
  onTransition,
}: {
  run: PayrollRun;
  totals: RunTotals;
  active: boolean;
  canChain: boolean;
  transitioning: boolean;
  timezone?: string | null;
  currency?: string | null;
  onToggle: () => void;
  onTransition: (action: "compute" | "approve" | "pay" | "lock" | "cancel") => void;
}) {
  return (
    <>
      <tr className="cursor-pointer hover:bg-slate-50" onClick={onToggle}>
        <td className="px-4 py-3">
          {active ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-slate-900">{run.run_number}</td>
        <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
          {formatDate(run.period_start, timezone ?? undefined)} – {formatDate(run.period_end, timezone ?? undefined)}
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-700">
          {totals.employee_count ?? 0}
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-700">
          {formatRawMoney(totals.gross, currency)}
        </td>
        <td className="px-4 py-3 text-right text-sm font-medium tabular-nums text-slate-900">
          {formatRawMoney(totals.net_pay, currency)}
        </td>
        <td className="px-4 py-3">
          <Badge tone={PAYROLL_TONE[run.status ?? "draft"]}>{PAYROLL_LABEL[run.status ?? "draft"]}</Badge>
        </td>
        <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
          <RunActions
            run={run}
            canChain={canChain}
            transitioning={transitioning}
            onTransition={onTransition}
          />
        </td>
      </tr>
      {active ? (
        <tr className="bg-slate-50/60">
          <td colSpan={8} className="px-4 py-4">
            <RunItems runId={run.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** The irreversible "close the books" prompt. Exported so the wording and the
 * confirm/keep wiring can be tested without mounting the whole register. */
export function LockConfirmation({
  runNumber,
  transitioning,
  onConfirm,
  onKeep,
}: {
  runNumber?: string;
  transitioning: boolean;
  onConfirm: () => void;
  onKeep: () => void;
}) {
  return (
    <Modal title="Lock payroll run" onClose={onKeep}>
      <p className="text-sm text-slate-700">
        Permanently close the books for{" "}
        <span className="font-semibold text-slate-900">{runNumber ?? "this run"}</span>?
      </p>
      <p className="mt-2 text-xs text-slate-500">
        Locking is permanent. The run, its payslips and its audit history stay
        readable, but it cannot be edited, recalculated, rolled back or reopened.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button disabled={transitioning} onClick={onKeep}>
          Keep
        </Button>
        <Button variant="primary" disabled={transitioning} onClick={onConfirm}>
          Confirm lock
        </Button>
      </div>
    </Modal>
  );
}

export function RunActions({
  run,
  canChain,
  transitioning,
  onTransition,
}: {
  run: PayrollRun;
  canChain: boolean;
  transitioning: boolean;
  onTransition: (action: "compute" | "approve" | "pay" | "lock" | "cancel") => void;
}) {
  const [confirmingLock, setConfirmingLock] = useState(false);
  const buttons: Array<{ action: "compute" | "approve" | "pay" | "lock" | "cancel"; label: string; variant?: "primary" }> = [];
  if (run.status === "draft") {
    buttons.push({ action: "compute", label: "Compute" });
    buttons.push({ action: "cancel", label: "Cancel" });
  } else if (run.status === "computed") {
    buttons.push({ action: "approve", label: "Approve" });
    buttons.push({ action: "cancel", label: "Cancel" });
  } else if (run.status === "approved") {
    buttons.push({ action: "pay", label: "Pay", variant: "primary" });
  } else if (run.status === "paid") {
    buttons.push({ action: "lock", label: "Lock" });
  }
  if (!canChain) {
    return <span className="text-xs text-slate-400">Admin action</span>;
  }
  if (buttons.length === 0) return null;
  if (confirmingLock) {
    return (
      <LockConfirmation
        runNumber={run.run_number}
        transitioning={transitioning}
        onConfirm={() => {
          setConfirmingLock(false);
          onTransition("lock");
        }}
        onKeep={() => setConfirmingLock(false)}
      />
    );
  }
  return (
    <span className="inline-flex gap-1.5">
      {buttons.map((button) => (
        <Button
          key={button.action}
          variant={button.variant ?? "secondary"}
          disabled={transitioning}
          onClick={() =>
            button.action === "lock" ? setConfirmingLock(true) : onTransition(button.action)
          }
        >
          {button.label}
        </Button>
      ))}
    </span>
  );
}

function RunItems({ runId }: { runId: string }) {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const items = useQuery({
    queryKey: ["payroll-items", runId],
    queryFn: ({ signal }) => api.payrollRunItems(runId, signal),
  });

  if (items.isPending) return <TableSkeleton rows={2} />;
  const rows: PayrollItem[] = items.data ?? [];
  if (rows.length === 0) {
    return <EmptyState title="Nothing computed yet" body="Computing this run creates one payslip per employee with a structure." />;
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <table className="w-full bg-white">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2" />
            <th className="px-3 py-2">Employee</th>
            <th className="px-3 py-2">Days</th>
            <th className="px-3 py-2 text-right">Gross</th>
            <th className="px-3 py-2 text-right">Net pay</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((item) => (
            <Fragment key={item.id}>
              <tr
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => setOpenItem(openItem === item.id ? null : item.id)}
              >
                <td className="px-3 py-2 text-slate-400">
                  {openItem === item.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </td>
                <td className="px-3 py-2">
                  <p className="text-sm font-medium text-slate-900">{item.employee_display}</p>
                  <p className="text-xs text-slate-500">{item.employee_code}</p>
                </td>
                <td className="px-3 py-2 text-sm tabular-nums text-slate-600">{formatDayCount(item.days_worked)}</td>
                <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-700">{formatMoney(item.gross)}</td>
                <td className="px-3 py-2 text-right text-sm font-medium tabular-nums text-slate-900">
                  {formatMoney(item.net_pay)}
                </td>
                <td className="px-3 py-2 text-sm capitalize text-slate-600">{item.status}</td>
              </tr>
              {openItem === item.id ? (
                <tr className="bg-slate-50/60">
                  <td colSpan={6} className="px-4 py-4">
                    <PayrollAttendancePanel itemId={item.id} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayrollAttendancePanel({ itemId }: { itemId: string }) {
  const query = useQuery({
    queryKey: ["payroll-item-attendance", itemId],
    queryFn: ({ signal }) => api.payrollItemAttendance(itemId, signal),
  });

  if (query.isPending) return <TableSkeleton rows={3} />;
  if (query.isError) {
    return <p className="text-sm text-rose-600">Could not load attendance for this payslip.</p>;
  }
  const data = query.data!;
  const s = data.summary;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-xs sm:grid-cols-6">
        <Metric label="Present" value={s.present} />
        <Metric label="Half day" value={s.half_day} />
        <Metric label="Paid leave" value={s.paid_leave} />
        <Metric label="Unpaid leave" value={s.unpaid_leave} />
        <Metric label="Absent" value={s.absent} />
        <Metric label="Weekly off" value={s.week_off} />
        <Metric label="Holiday" value={s.holiday} />
        <Metric label="Worked" value={formatMinutes(s.worked_minutes)} />
        <Metric label="Overtime" value={formatMinutes(s.overtime_minutes)} />
        <Metric label="Late days" value={s.late_count} />
        <Metric label="Scheduled" value={s.scheduled_days} />
        <Metric label="Exceptions" value={s.exceptions} tone={s.exceptions ? "rose" : undefined} />
      </div>
      <div className="overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
              <th className="px-2 py-1.5">Date</th>
              <th className="px-2 py-1.5">Shift</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">In</th>
              <th className="px-2 py-1.5">Out</th>
              <th className="px-2 py-1.5">Worked</th>
              <th className="px-2 py-1.5">Leave</th>
              <th className="px-2 py-1.5">Impact</th>
              <th className="px-2 py-1.5">Exception</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.days.map((day) => (
              <tr key={day.date} className={day.exception ? "bg-rose-50/40" : undefined}>
                <td className="px-2 py-1.5 text-xs tabular-nums text-slate-600">{day.date}</td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{day.shift || "—"}</td>
                <td className="px-2 py-1.5 text-xs capitalize text-slate-600">{day.status?.replace(/_/g, " ") || "—"}</td>
                <td className="px-2 py-1.5 text-xs tabular-nums text-slate-500">{timeOf(day.clock_in_at)}</td>
                <td className="px-2 py-1.5 text-xs tabular-nums text-slate-500">{timeOf(day.clock_out_at)}</td>
                <td className="px-2 py-1.5 text-xs tabular-nums text-slate-600">{formatMinutes(day.worked_minutes)}</td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{day.leave || "—"}</td>
                <td className="px-2 py-1.5 text-xs text-slate-600">{day.payroll_impact}</td>
                <td className="px-2 py-1.5 text-xs text-rose-600">{day.exception || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: number | string; tone?: "rose" }) {
  return (
    <div>
      <p className="text-slate-500">{label}</p>
      <p className={`font-medium tabular-nums ${tone === "rose" ? "text-rose-600" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function timeOf(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(date);
}

function CreateRun({ pending, onSubmit }: { pending: boolean; onSubmit: (periodStart: string) => void }) {
  const [periodStart, setPeriodStart] = useState("");
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!periodStart) return;
        onSubmit(periodStart);
        setPeriodStart("");
      }}
      className="flex items-center gap-2"
    >
      <input
        type="date"
        value={periodStart}
        required
        onChange={(event) => setPeriodStart(event.target.value)}
        aria-label="Period start"
        className="rounded-md border-0 bg-white px-3 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
      />
      <Button type="submit" variant="primary" disabled={pending}>
        <Plus size={15} /> New run
      </Button>
    </form>
  );
}

/** Who may keep the run moving, by current state. */
function transitions(status: PayrollRun["status"], isAdmin: boolean): boolean {
  if (status === "draft" || status === "computed" || status === "approved") return true;
  return isAdmin; // paid/locked/cancelled need the book-keeper to reopen.
}

function errorMessage(error: unknown): string | null {
  return error instanceof ApiError ? error.message : null;
}

/** Format a raw decimal string into the account currency. */
function formatRawMoney(value: string | undefined, currency?: string | null): string {
  const num = Number(value ?? 0);
  if (value === undefined || value === null || Number.isNaN(num)) return "—";
  try {
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : undefined, {
      style: "currency",
      currency: currency ?? "INR",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency ?? "INR"} ${num.toFixed(2)}`;
  }
}